import { useEffect, useMemo } from 'react'
import { useFlag, useNewCode } from '@calendly/feature-check-react'

import { consentModule } from 'services/consent/consent_module'
import {
  handleConsentStateRequest,
  publishConsentState,
  setVendorRuntimeContext,
} from 'services/consent/consent_state_publish'
import { onVendorRuntimeChange } from 'services/vendors/vendor_runtime'

/**
 * Exposes the iframe's resolved `ConsentState` to the embedding host.
 *
 * Wiring:
 *   - Listens for `calendly.consent.state.get.v1` messages from the parent.
 *     The transport verifies the message origin against the URL's `embed_domain`
 *     before responding (same trust model as `ConsentUpdateMessageListener`).
 *   - Subscribes to `consentModule.onChange` and vendor-runtime changes, forwarding
 *     every resolved-state or vendor-activation update to the verified parent.
 *
 * Gating: behind the same `sa_consent_aware_embed` flag as the host-side
 * listener so observability rolls out alongside the input it observes.
 */
export const ConsentStatePublisher = () => {
  const consentModuleEnabled = useFlag('sa_consent_aware_embed', false)
  const bookingStorageEnabled = useNewCode('growth_booking_data_storage')?.enabled
  const vendorContext = useMemo(() => ({
    featureFlags: { growth_booking_data_storage: bookingStorageEnabled },
  }), [bookingStorageEnabled])

  useEffect(() => {
    setVendorRuntimeContext(vendorContext)
    if (consentModuleEnabled) {
      publishConsentState(consentModule.getState())
    }
  }, [vendorContext, consentModuleEnabled])

  useEffect(() => {
    if (!consentModuleEnabled) return undefined

    const handler = (event) => {
      handleConsentStateRequest(event, consentModule.getState)
    }
    const republish = () => publishConsentState(consentModule.getState())

    window.addEventListener('message', handler)
    const unsubscribeConsent = consentModule.onChange(publishConsentState)
    const unsubscribeVendor = onVendorRuntimeChange(republish)
    return () => {
      window.removeEventListener('message', handler)
      unsubscribeConsent()
      unsubscribeVendor()
    }
  }, [consentModuleEnabled])

  return null
}
